import { useContext, useRef, useState } from 'react';
import { Divider } from 'primereact/divider';
import { Dropdown } from 'primereact/dropdown';
import { QueryClient, useQuery, useMutation } from 'react-query';
import MlxButton from '../../../components/common/MlxButton/MlxButton';
import MlxToast from '../../../components/common/MlxToast/MlxToast';
import MlxConfirmPopup from '../../../components/common/MlxConfirmPopup/MlxConfirmPopup';
import withSidebar from '../../../hoc/withSideBar/withSideBar';
import AddFeatureSidebar from './components/AddFeatureSidebar';
import FeaturesList from './components/FeaturesList';
import { FEATURE_LIST } from '../../../constants/queryKeys';
import { _Feature, getFeatureList, syncFeaturesFromStripe } from '../../../helpers/subscriptionFeatureHelper';
import { _ToastMessageProps } from '../../../constants/staticTypes';
import { defaultToastData } from '../../../helpers/commonHelper';
import { CommonCtx } from '../../../context/CommonCtxProvider';
import { AlertVariant } from '../../../constants/constants';
import logger from '../../../utils/logger';
import './FeaturesManagement.scss';

function FeaturesManagement() {
    const queryClient = new QueryClient();
    const { showSideBar, setShowSideBar } = useContext(CommonCtx);

    const [toastMessage, setToastMessage] = useState<_ToastMessageProps>(defaultToastData);
    const [features, setFeatures] = useState<_Feature[]>([]);
    const [editFeature, setEditFeature] = useState<_Feature>();
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [statusFilter, setStatusFilter] = useState<string>('Active');

    // Determine isActive parameter based on statusFilter
    const getIsActiveFilter = (): boolean | undefined => {
        if (statusFilter === 'Active') return true;
        if (statusFilter === 'Archive') return false;
        return undefined; // 'All' case - no filter
    };

    const featureListQuery = useQuery({
        queryKey: [FEATURE_LIST, currentPage, rowsPerPage, statusFilter],
        queryFn: () => getFeatureList(queryClient, currentPage, rowsPerPage, getIsActiveFilter()),
        onSuccess: (response) => {
            const responseData = response?.data?.data?.data as _Feature[];
            setFeatures(responseData || []);
        },
        onError: (error) => logger.log('Failed to fetch feature list', error),
        refetchOnWindowFocus: false,
        keepPreviousData: true,
    });
    const featureListQueryRef = useRef(featureListQuery);

    // Get pagination data and total records
    const paginationData = featureListQuery.data?.data?.data?.pagination;
    const totalRecords = paginationData?.total || 0;

    const onPageChange = (event: any) => {
        setCurrentPage(event.page + 1); // PrimeReact uses 0-based page index
        setRowsPerPage(event.rows);
    };

    const statusOptions = [
        { label: 'All', value: 'All' },
        { label: 'Active', value: 'Active' },
        { label: 'Archived', value: 'Archive' },
    ];

    // Sync features mutation
    const syncFeaturesMutation = useMutation({
        mutationFn: () => syncFeaturesFromStripe(queryClient),
        onSuccess: (response: any) => {
            setToastMessage({
                show: true,
                message: response?.data?.message || 'Features synced successfully from Stripe',
                variant: AlertVariant.SUCCESS,
            });
            featureListQuery.refetch();
        },
        onError: (error: any) => {
            logger.log('Failed to sync features from Stripe', error);
            setToastMessage({
                show: true,
                message: error?.response?.data?.message || 'Failed to sync features from Stripe. Please try again.',
                variant: AlertVariant.ERROR,
            });
        },
    });

    const handleSyncFromStripe = () => {
        syncFeaturesMutation.mutate();
    };

    const handleAddFeature = () => {
        setShowSideBar(true);
        setEditFeature(undefined);
    };

    const handleEdit = (feature: _Feature) => {
        setEditFeature(feature);
        setShowSideBar(true);
    };

    return (
        <>
            <MlxToast
                message={toastMessage.message}
                onClose={() => setToastMessage(defaultToastData)}
                show={toastMessage.show}
                variant={toastMessage.variant}
            />
            <MlxConfirmPopup />

            <AddFeatureSidebar
                showSideBar={showSideBar}
                setShowSideBar={setShowSideBar}
                feature={editFeature}
                setEditFeature={setEditFeature}
                featureListQueryRef={featureListQueryRef}
                setToastMessage={setToastMessage}
            />

            <div className="page-section">
                <span className="page-header">Features Management</span>
                <div className="page-content h-100">
                    <div className="content-header flex-grow-0">
                        <span className="header-title">Subscription Features</span>
                        <div className="header-fields">
                            <>
                                <Dropdown
                                    value={statusFilter}
                                    options={statusOptions}
                                    onChange={(e) => {
                                        setStatusFilter(e.value);
                                        setCurrentPage(1); // Reset to first page when filter changes
                                    }}
                                    className="status-filter me-2 p-inputtext-sm"
                                    style={{ minWidth: '120px', height: '2.5rem', display: 'flex', alignItems: 'center' }}
                                />
                                <MlxButton
                                    icon="ri-refresh-line"
                                    size="small"
                                    outlined
                                    onClick={handleSyncFromStripe}
                                    className="sync-stripe-btn"
                                    loading={syncFeaturesMutation.isLoading}
                                    disabled={syncFeaturesMutation.isLoading}
                                >
                                    <span className="sync-label">Sync from Stripe</span>
                                </MlxButton>
                                <MlxButton
                                    className="ms-2"
                                    removeLabelOnMobile={true}
                                    size="small"
                                    label="Add Feature"
                                    icon="ri-add-large-line"
                                    iconPos="right"
                                    onClick={handleAddFeature}
                                />
                            </>
                        </div>
                    </div>
                    <Divider className="content-divider" />
                    <div className="content-body flex-grow-1 overflow-hidden">
                        <FeaturesList
                            features={features}
                            featureListQuery={featureListQuery}
                            onEdit={handleEdit}
                            currentPage={currentPage}
                            rowsPerPage={rowsPerPage}
                            totalRecords={totalRecords}
                            onPageChange={onPageChange}
                        />
                    </div>
                </div>
            </div>
        </>
    );
}

export default withSidebar(FeaturesManagement);
